import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Save, Fingerprint, Lock, Undo2 } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  SPORTS_CONFIG,
  getSportStats,
  calculateSportPoints,
  getInitialStats,
  getPrimaryStatLabel,
  hasBattingPitching,
  getStatsByCategory
} from '../config/sports';

// Individual Stat Button with proper long-press handling
function StatButton({ stat, count, onIncrement, onDecrement }) {
  const pressTimer = useRef(null);
  const isLongPress = useRef(false);
  const isTouchDevice = useRef(false);
  const lastTouchTime = useRef(0);

  const handleTouchStart = useCallback((e) => {
    isTouchDevice.current = true;
    isLongPress.current = false;
    
    pressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      if (count > 0) {
        onDecrement();
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
      }
    }, 500);
  }, [count, onDecrement]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault(); // Prevent synthetic click
    lastTouchTime.current = Date.now();
    
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    
    // Only increment if it was a quick tap, not a long press
    if (!isLongPress.current) {
      onIncrement();
    }
    
    isLongPress.current = false;
  }, [onIncrement]);

  const handleTouchCancel = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
    isLongPress.current = false;
  }, []);

  const handleClick = useCallback((e) => {
    // Block click if it's from a touch device or recent touch
    if (isTouchDevice.current || (Date.now() - lastTouchTime.current) < 500) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Only handle for actual mouse clicks
    onIncrement();
  }, [onIncrement]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    if (count > 0) {
      onDecrement();
    }
  }, [count, onDecrement]);

  return (
    <button
      data-testid={`stat-btn-${stat.key}`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className={`relative bg-[#1A1A1A] rounded-xl p-4 border-2 transition-all select-none ${
        count > 0 ? 'border-current' : 'border-[#333]'
      }`}
      style={{ 
        borderColor: count > 0 ? stat.color : '#333', 
        backgroundColor: count > 0 ? `${stat.color}15` : '#1A1A1A',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation'
      }}
    >
      {count > 0 && (
        <span
          className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-black"
          style={{ backgroundColor: stat.color }}
        >
          {count}
        </span>
      )}
      <span className="text-xs font-bold block" style={{ color: stat.color }}>
        {stat.label.toUpperCase()}
      </span>
      {stat.pointValue && (
        <span className="text-xs text-gray-500 block mt-1">+{stat.pointValue} pt</span>
      )}
    </button>
  );
}

// Stat Buttons Grid
function StatButtonsGrid({ displayStats, stats, incrementStat, decrementStat }) {
  return (
    <div className="grid grid-cols-3 gap-2 mb-4">
      {displayStats.map(stat => (
        <StatButton
          key={stat.key}
          stat={stat}
          count={stats[stat.key] || 0}
          onIncrement={() => incrementStat(stat.key)}
          onDecrement={() => decrementStat(stat.key)}
        />
      ))}
    </div>
  );
}

export default function NewGame() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [selectedSport, setSelectedSport] = useState(null);
  const [stats, setStats] = useState({});
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [statMode, setStatMode] = useState('batting');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchData();
  }, [athleteId]);

  const fetchData = async () => {
    try {
      const [athleteRes, subRes] = await Promise.all([
        api.get(`/athletes/${athleteId}`),
        api.get('/subscription/status')
      ]);
      setAthlete(athleteRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/athletes');
    }
  };

  // Get allowed sports based on subscription
  const getAllowedSports = () => {
    if (!subscription?.is_subscribed) {
      // Free trial users can use all sports from their athlete's configured sports
      return athlete?.sports || [];
    }
    
    // CRITICAL: Subscribed users must respect their plan's sports limit
    const subSports = subscription?.selected_sports || [];
    const sportsLimit = subscription?.sports_limit || 6;
    
    // If user has a limited plan (2 or 4 sports), enforce the restriction
    if (sportsLimit < 6) {
      // If no sports are selected in subscription, use the first N from the default list
      const allowedFromPlan = subSports.length > 0 
        ? subSports.slice(0, sportsLimit) 
        : ['basketball', 'soccer', 'volleyball', 'football', 'baseball', 'softball'].slice(0, sportsLimit);
      
      // Return only sports that are in both the athlete's sports AND the allowed plan sports
      return (athlete?.sports || []).filter(s => allowedFromPlan.includes(s));
    }
    
    // All sports plan - allow everything the athlete has
    return athlete?.sports || [];
  };

  const allowedSports = getAllowedSports();

  const handleSelectSport = (sport) => {
    if (!allowedSports.includes(sport)) {
      toast.error(`Your subscription doesn't include ${SPORTS_CONFIG[sport]?.name}. Upgrade your plan to access this sport.`);
      return;
    }
    setSelectedSport(sport);
    setStats(getInitialStats(sport));
    setStatMode('batting');
  };

  const incrementStat = (statKey) => {
    setStats(prev => ({ ...prev, [statKey]: (prev[statKey] || 0) + 1 }));
  };

  const decrementStat = (statKey) => {
    setStats(prev => {
      const current = prev[statKey] || 0;
      if (current > 0) {
        toast.info('Stat removed');
        return { ...prev, [statKey]: current - 1 };
      }
      return prev;
    });
  };

  const handleSaveGame = async () => {
    const hasAnyStats = Object.values(stats).some(v => v > 0);
    if (!hasAnyStats) {
      toast.error('Please record at least one stat');
      return;
    }

    setIsSaving(true);
    try {
      await api.post('/games', {
        athlete_id: athleteId,
        sport: selectedSport,
        stats,
        notes: notes || null
      });
      toast.success('Game saved!');
      refreshUser();
      navigate(`/athlete/${athleteId}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.info('Free trial ended. Subscribe to continue tracking games.');
        navigate('/paywall');
      } else {
        toast.error('Failed to save game');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!athlete) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5E5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Sport Selection Screen
  if (!selectedSport) {
    return (
      <div className="min-h-screen bg-[#000] p-4">
        <div className="flex items-center mb-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate(`/athlete/${athleteId}`)}
            className="flex items-center gap-1 text-white"
          >
            <ChevronLeft className="w-6 h-6" />
            <span>Back</span>
          </button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">New Game</h1>
          <p className="text-gray-400">Select Sport</p>
          {subscription?.is_subscribed && allowedSports.length < (athlete.sports?.length || 0) && (
            <p className="text-xs text-yellow-500 mt-2">
              Some sports are locked. Upgrade your plan for more sports.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {athlete.sports?.map(sport => {
            const config = SPORTS_CONFIG[sport];
            const isAllowed = allowedSports.includes(sport);
            return (
              <button
                key={sport}
                data-testid={`select-sport-${sport}`}
                onClick={() => handleSelectSport(sport)}
                className={`bg-[#1A1A1A] rounded-2xl p-6 flex flex-col items-center gap-2 transition-colors border-2 border-transparent relative ${
                  isAllowed 
                    ? 'hover:bg-[#252525] hover:border-[#00E5E5]' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                {!isAllowed && (
                  <div className="absolute top-2 right-2 bg-[#333] rounded-full p-1">
                    <Lock className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <span className="text-5xl">{config?.emoji}</span>
                <span className="text-white font-semibold">{config?.name}</span>
                {!isAllowed && (
                  <span className="text-xs text-gray-500">Not in plan</span>
                )}
              </button>
            );
          })}
        </div>
        
        {subscription?.is_subscribed && allowedSports.length < (athlete.sports?.length || 0) && (
          <button
            onClick={() => navigate('/paywall')}
            className="w-full mt-6 bg-[#1A1A1A] text-[#00E5E5] font-semibold py-3 rounded-xl border border-[#00E5E5]/30"
          >
            Upgrade Plan for More Sports
          </button>
        )}
      </div>
    );
  }

  // Stat Tracking Screen
  const sportConfig = SPORTS_CONFIG[selectedSport];
  const sportStats = getSportStats(selectedSport);
  const points = calculateSportPoints(selectedSport, stats);
  const primaryLabel = getPrimaryStatLabel(selectedSport);
  
  const displayStats = hasBattingPitching(selectedSport)
    ? getStatsByCategory(selectedSport, statMode)
    : sportStats;

  return (
    <div className="min-h-screen bg-[#000] pb-24">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          data-testid="back-btn"
          onClick={() => setSelectedSport(null)}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
        <span className="text-white font-semibold">
          {sportConfig?.emoji} {sportConfig?.name}
        </span>
        <div className="w-16"></div>
      </div>

      <div className="px-4">
        {/* Batting/Pitching Toggle */}
        {hasBattingPitching(selectedSport) && (
          <div className="flex gap-2 mb-4">
            <button
              data-testid="batting-mode-btn"
              onClick={() => setStatMode('batting')}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                statMode === 'batting'
                  ? 'bg-[#00E5E5] text-black'
                  : 'bg-[#1A1A1A] text-gray-400'
              }`}
            >
              ⚾ BATTING
            </button>
            <button
              data-testid="pitching-mode-btn"
              onClick={() => setStatMode('pitching')}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                statMode === 'pitching'
                  ? 'bg-[#00E5E5] text-black'
                  : 'bg-[#1A1A1A] text-gray-400'
              }`}
            >
              🥎 PITCHING
            </button>
          </div>
        )}

        {/* Points Display */}
        <div className="bg-[#1A1A1A] rounded-2xl p-6 mb-4">
          <div className="text-center mb-4 pb-4 border-b border-[#333]">
            <p className="text-6xl font-black text-[#00E5E5]">{points}</p>
            <p className="text-white font-bold tracking-widest mt-1">{primaryLabel}</p>
          </div>
          
          {/* Stats Summary */}
          <div className="flex flex-wrap justify-center gap-4">
            {sportStats.slice(0, 10).map(stat => (
              <div key={stat.key} className="text-center min-w-[50px]">
                <p className="text-xl font-bold" style={{ color: stat.color }}>
                  {stats[stat.key] || 0}
                </p>
                <p className="text-xs text-gray-400">{stat.shortLabel}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stat Buttons Grid */}
        <StatButtonsGrid 
          displayStats={displayStats}
          stats={stats}
          incrementStat={incrementStat}
          decrementStat={decrementStat}
        />

        {/* Instructions */}
        <div className="text-center mb-4 space-y-1">
          <p className="text-xs text-gray-500">
            <Fingerprint className="inline w-3 h-3 mr-1" />
            Tap to add stat
          </p>
          <p className="text-xs text-[#00E5E5] font-semibold">
            <Undo2 className="inline w-3 h-3 mr-1" />
            Hold to undo mistake
          </p>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <p className="text-xs text-gray-400 font-semibold mb-2 tracking-wider">GAME NOTES</p>
          <textarea
            data-testid="game-notes-input"
            placeholder="Add notes about this game..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-[#1A1A1A] text-white rounded-xl p-4 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
        <button
          data-testid="save-game-btn"
          onClick={handleSaveGame}
          disabled={isSaving}
          className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#5FFBFB] transition-colors disabled:opacity-50"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Saving...' : 'SAVE GAME'}
        </button>
      </div>
    </div>
  );
}
